import { csrfCookie } from '../../shared/kit';
import type { RentalAvailability, RentalCalendar } from './types';

let REST_BASE = '';
let NONCE = '';

export function configureApi(restBase: string, nonce: string): void {
    REST_BASE = restBase.replace(/\/$/, '');
    NONCE = nonce;
}

export interface RelatedItemSummary {
    id: number;
    name: string;
    slug: string;
    image_url: string | null;
    price_indicative: unknown;
    quantity?: number;
}
export interface ReserveItemDetail {
    id: number;
    name: string;
    is_kit: boolean;
    addons: RelatedItemSummary[];
    kit_components: RelatedItemSummary[];
}

function isLeanTransport(): boolean {
    return String(NONCE || '').trim() === '';
}

function authHeaders(method: string, idempotencyKey?: string): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const nonce = String(NONCE || '').trim();
    if (nonce !== '') {
        headers['X-WP-Nonce'] = nonce;
    }
    if (method !== 'GET' && method !== 'HEAD') {
        const csrf = csrfCookie();
        if (csrf !== '') {
            headers['X-GAS-CSRF'] = csrf;
        } else if (nonce === '') {
            throw new Error('Jeton de sécurité indisponible');
        }
        if (idempotencyKey) {
            headers['Idempotency-Key'] = idempotencyKey;
        }
    }
    return headers;
}

function newIdempotencyKey(): string {
    const webCrypto = globalThis.crypto;
    if (typeof webCrypto?.randomUUID === 'function') {
        return `rental:${webCrypto.randomUUID()}`;
    }
    if (typeof webCrypto?.getRandomValues === 'function') {
        const bytes = new Uint8Array(24);
        webCrypto.getRandomValues(bytes);
        return `rental:${Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')}`;
    }
    return `rental:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function parseJson(res: Response): Promise<any> {
    return res.json().catch(() => ({}));
}

export async function fetchItem(itemId: number): Promise<ReserveItemDetail> {
    const res = await fetch(`${REST_BASE}/items/${itemId}`, {
        headers: authHeaders('GET'),
        credentials: 'same-origin',
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as ReserveItemDetail;
}

export async function fetchAvailability(itemId: number, start: string, end: string): Promise<RentalAvailability> {
    const qs = `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
    const res = await fetch(`${REST_BASE}/items/${itemId}/availability${qs}`, {
        headers: authHeaders('GET'),
        credentials: 'same-origin',
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as RentalAvailability;
}

export interface OpeningHours {
    pickup_mode: string;
    windows: Array<{ day_of_week: number; start_time: string; end_time: string; scope: string; scope_id: number }>;
    holidays: string[];
}

export async function fetchOpeningHours(): Promise<OpeningHours> {
    const res = await fetch(`${REST_BASE}/opening-hours`, {
        headers: authHeaders('GET'),
        credentials: 'same-origin',
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as OpeningHours;
}

export async function fetchCalendar(itemId: number, from: string, to: string): Promise<RentalCalendar> {
    const qs = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const res = await fetch(`${REST_BASE}/items/${itemId}/calendar${qs}`, {
        headers: authHeaders('GET'),
        credentials: 'same-origin',
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as RentalCalendar;
}

export async function joinWaitlist(
    itemId: number,
    start: string,
    end: string,
    idempotencyKey = newIdempotencyKey(),
): Promise<{ ok: boolean; already?: boolean }> {
    const res = await fetch(`${REST_BASE}/items/${itemId}/waitlist`, {
        method: 'POST',
        headers: { ...authHeaders('POST', idempotencyKey), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ start, end }),
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || json?.data?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as { ok: boolean; already?: boolean };
}

export async function createReservation(
    itemId: number,
    start: string,
    end: string,
    quantity: number,
    termsAccepted = false,
    idempotencyKey = newIdempotencyKey(),
    queryId = '',
): Promise<{ reservation_id: number }> {
    const body: Record<string, unknown> = {
        start_at: start,
        end_at: end,
        lines: [{ item_id: itemId, quantity }],
        terms_accepted: termsAccepted,
    };
    const q = String(queryId || '').trim();
    if (q !== '') {
        body.query_id = q;
    }
    const res = await fetch(`${REST_BASE}/reservations`, {
        method: 'POST',
        headers: { ...authHeaders('POST', idempotencyKey), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    });
    const json = await parseJson(res);
    if (!res.ok) {
        throw new Error(json?.message || json?.data?.message || `HTTP ${res.status}`);
    }
    return (json?.data ?? json) as { reservation_id: number };
}

/** Empreinte logique d'une tentative de réservation unitaire. */
export function fingerprintReserveAttempt(parts: {
    item_id: number;
    start_at: string;
    end_at: string;
    quantity?: number;
    terms_accepted: boolean;
    query_id?: string;
}): string {
    return JSON.stringify({
        item_id: parts.item_id,
        start_at: parts.start_at,
        end_at: parts.end_at,
        quantity: parts.quantity ?? 1,
        terms_accepted: !!parts.terms_accepted,
        query_id: String(parts.query_id ?? '').trim(),
    });
}

export type AttemptKeyState = { fingerprint: string; key: string };

/** Conserve la clé si l'empreinte est identique ; sinon en émet une nouvelle. */
export function resolveAttemptKey(
    current: AttemptKeyState | null,
    fingerprint: string,
    mint: () => string = newIdempotencyKey,
): AttemptKeyState {
    if (current && current.fingerprint === fingerprint) {
        return current;
    }
    return { fingerprint, key: mint() };
}

export { isLeanTransport, newIdempotencyKey };
